import { LoaderCircle } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from '../../app/auth';
import { Routes } from '../../app/routes';
import { flatten, useInbox, useWorkspaces } from '../../data/queries';
import type { EmployeeSummary, Item, Workspace } from '../../domain/models';
import { firstName } from '../../domain/models';
import { useT, type Translate } from '../../l10n/i18n';
import { AvatarStack, EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { EmptyState, ErrorView, Skeleton } from '../core/widgets/common';
import { ItemView } from '../item/ItemView';
import { EmployeeWorkBlock, WorkBlockSkeleton } from './WorkBlock';
import { OfferMarkSeen } from './WorkEntries';
import { displayOrder, foldKeyOf, groupByEmployee, nextAfter } from './workGroups';
import './inbox.css';

// Everything that needs the person, ported from the mobile app's inbox_screen.dart. On wide screens the
// list sits on the left and the open item on the right, so a person can work through the day without
// going back and forth; on narrow ones the list and the item take turns, as on a phone.

/** From this width the item opens next to the list. */
const splitQuery = '(min-width: 1100px)';

function subscribeSplit(onChange: () => void) {
  if (typeof window.matchMedia !== 'function') return () => {};
  const m = window.matchMedia(splitQuery);
  m.addEventListener('change', onChange);
  return () => m.removeEventListener('change', onChange);
}
const isSplit = () => typeof window.matchMedia === 'function' && window.matchMedia(splitQuery).matches;
const useSplit = () => useSyncExternalStore(subscribeSplit, isSplit, () => false);

function greeting(t: Translate, name: string | undefined): string {
  if (!name) return t('greetingNoName');
  const h = new Date().getHours();
  if (h < 11) return t('greetingMorning', { name });
  if (h < 18) return t('greetingDay', { name });
  return t('greetingEvening', { name });
}

/** Who is waiting on the person, most items first. */
function waitingEmployees(items: Item[]): EmployeeSummary[] {
  // By character, not by employee: one Rūta working for three projects is one face.
  const counts = new Map<string, { e: EmployeeSummary; n: number }>();
  for (const i of items) {
    const c = counts.get(i.employee.role);
    counts.set(i.employee.role, { e: c?.e ?? i.employee, n: (c?.n ?? 0) + 1 });
  }
  return [...counts.values()].sort((a, b) => b.n - a.n).map((c) => c.e);
}

/** A row's link, by the item it opens. */
function rowLink(root: HTMLElement | null, id: string): HTMLAnchorElement | undefined {
  return [...(root?.querySelectorAll<HTMLAnchorElement>('a[data-item-id]') ?? [])].find((a) => a.dataset.itemId === id);
}

/** Where the keyboard is in the list: on an item's line (its link or its button), or on a fold's line. */
function listPosition(target: EventTarget | null): { focused?: string; onFold?: string } {
  if (!(target instanceof HTMLElement)) return {};
  const fold = target.closest<HTMLElement>('[data-first-item]');
  if (fold) return { onFold: fold.dataset.firstItem };
  return { focused: target.closest('.work-row')?.querySelector<HTMLAnchorElement>('a[data-item-id]')?.dataset.itemId };
}

/** Keys typed into a field, or while a dialog is open, are not shortcuts. */
function shortcutsOff(target: EventTarget | null): boolean {
  if (document.querySelector('dialog[open], [aria-modal="true"]')) return true;
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement) return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(target.type);
  return target.closest('[role="textbox"], [contenteditable="true"]') !== null;
}

export function InboxPage() {
  const t = useT();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const split = useSplit();
  const { session } = useAuth();

  const [workspaceId, setWorkspaceId] = useState<string>();
  const inbox = useInbox({ workspaceId });
  const workspaces = useWorkspaces().data ?? [];
  const items = useMemo(() => flatten(inbox.data), [inbox.data]);
  const groups = useMemo(() => (items ? groupByEmployee(items) : undefined), [items]);
  const order = useMemo(() => (groups ? displayOrder(groups).map((i) => i.id) : []), [groups]);

  // Folds start closed and stay as the person left them across refreshes (their keys are stable). The
  // fold holding the open item shows it, unless the person closed it.
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const [shut, setShut] = useState<ReadonlySet<string>>(() => new Set());
  const selectedFold = useMemo(() => (groups && itemId ? foldKeyOf(groups, itemId) : undefined), [groups, itemId]);
  const isOpen = useCallback(
    (key: string) => open.has(key) || (key === selectedFold && !shut.has(key)),
    [open, shut, selectedFold],
  );
  const toggle = useCallback(
    (key: string) => {
      const closing = isOpen(key);
      const without = (s: ReadonlySet<string>) => {
        const next = new Set(s);
        next.delete(key);
        return next;
      };
      setOpen((s) => (closing ? without(s) : new Set(s).add(key)));
      setShut((s) => (closing ? new Set(s).add(key) : without(s)));
    },
    [isOpen],
  );

  const listRef = useRef<HTMLDivElement>(null);
  const [focusRequest, setFocusRequest] = useState<{ id: string }>();

  /** Shows an item: opens its fold, opens it next to the list on wide screens, and (for the keyboard)
   * moves focus to its line. */
  const goTo = (id: string, { focus }: { focus: boolean }) => {
    const fold = groups && foldKeyOf(groups, id);
    if (fold && !isOpen(fold)) toggle(fold);
    if (split) void navigate(Routes.inboxItem(id), { replace: Boolean(itemId) });
    if (focus) setFocusRequest({ id });
  };

  useEffect(() => {
    if (focusRequest) rowLink(listRef.current, focusRequest.id)?.focus();
  }, [focusRequest]);

  // The open item's line stays in sight, also when the next one opens after a decision.
  const loaded = groups !== undefined;
  useEffect(() => {
    if (split && itemId && loaded) rowLink(listRef.current, itemId)?.scrollIntoView?.({ block: 'nearest' });
  }, [split, itemId, loaded]);

  // The list as it was while the open item was on it: a decision refreshes the list before the item
  // view says it is done, and "next" means next in what the person was looking at.
  const live = useRef({ order: [] as string[], before: [] as string[] });
  useEffect(() => {
    live.current.order = order;
    if (itemId && order.includes(itemId)) live.current.before = order;
  }, [order, itemId]);

  const finished = (id: string) => {
    if (!split) {
      // As on a phone: back to the list, where the person left it.
      void navigate(Routes.inbox, { replace: true });
      return;
    }
    const { order: now, before } = live.current;
    const next = nextAfter(before.includes(id) ? before : now, id, now);
    if (next) goTo(next, { focus: false });
    else void navigate(Routes.inbox, { replace: true });
  };

  // j/k (and the arrows inside the list) move through the work, o opens the focused line; Enter on a
  // focused line opens it as any link does. No key decides anything.
  const onKey = useEffectEvent((e: KeyboardEvent) => {
    if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || shortcutsOff(e.target)) return;
    const inList = e.target instanceof Node && listRef.current?.contains(e.target) === true;
    const { focused, onFold } = listPosition(e.target);
    let step = 0;
    if (e.key === 'j' || (e.key === 'ArrowDown' && inList)) step = 1;
    else if (e.key === 'k' || (e.key === 'ArrowUp' && inList)) step = -1;
    else if (e.key === 'o' && focused) {
      e.preventDefault();
      void navigate(Routes.inboxItem(focused));
      return;
    }
    if (step === 0 || (!split && itemId) || order.length === 0) return;
    e.preventDefault();
    const from = onFold ?? focused ?? itemId;
    const at = from ? order.indexOf(from) : -1;
    // From a fold's own line, down is its first item and up the line before it.
    const to = at < 0 ? 0 : Math.min(Math.max(at + (onFold && step > 0 ? 0 : step), 0), order.length - 1);
    if (step > 0 && at === order.length - 1 && inbox.hasNextPage && !inbox.isFetchingNextPage) void inbox.fetchNextPage();
    goTo(order[to]!, { focus: true });
  });
  useEffect(() => {
    const handler = (e: KeyboardEvent) => onKey(e);
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // On narrow screens the list and the item take turns in the window: the item starts at its top and
  // the list comes back where the person left it. (Layout effects, so the listener is gone before the
  // shorter page clamps the scroll position.)
  const listScroll = useRef(0);
  const showsItemAlone = !split && Boolean(itemId);
  useLayoutEffect(() => {
    if (showsItemAlone) {
      window.scrollTo(0, 0);
      return;
    }
    window.scrollTo(0, listScroll.current);
    const save = () => {
      listScroll.current = window.scrollY;
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [showsItemAlone, itemId]);

  // The item alone fills the window as its own page does: its back link, its bar above the navigation.
  if (showsItemAlone && itemId) {
    return <ItemView key={itemId} itemId={itemId} layout="page" onFinished={() => finished(itemId)} />;
  }

  const list = (
    <div ref={listRef} className="inbox-list">
      <InboxHeader name={session ? firstName(session) : undefined} items={items} />
      {workspaces.length > 1 && <WorkspaceFilter workspaces={workspaces} selected={workspaceId} onSelect={setWorkspaceId} />}
      <div className="inbox-blocks">
        {!items && inbox.isError ? (
          <ErrorView error={inbox.error} onRetry={() => void inbox.refetch()} />
        ) : !groups ? (
          [0, 1, 2, 3].map((n) => <WorkBlockSkeleton key={n} />)
        ) : groups.length === 0 ? (
          <EmptyState art={<RestingTeam />} title={t('inboxEmptyTitle')} body={t('inboxEmptyBody')} />
        ) : (
          <OfferMarkSeen>
            {groups.map((work) => (
              <EmployeeWorkBlock
                key={work.employee.id}
                work={work}
                showWorkspace={workspaceId === undefined}
                isOpen={isOpen}
                toggle={toggle}
                itemHref={Routes.inboxItem}
                selectedId={split ? itemId : undefined}
              />
            ))}
            {inbox.hasNextPage && (
              <LoadMore loading={inbox.isFetchingNextPage} onVisible={() => void inbox.fetchNextPage()} />
            )}
          </OfferMarkSeen>
        )}
      </div>
    </div>
  );

  if (!split) return <div className="inbox">{list}</div>;
  return (
    <div className="inbox inbox-split">
      {list}
      <section className="inbox-pane">
        {itemId ? (
          <ItemView key={itemId} itemId={itemId} layout="pane" onFinished={() => finished(itemId)} />
        ) : (
          <PanePlaceholder items={items} />
        )}
      </section>
    </div>
  );
}

function InboxHeader({ name, items }: { name?: string; items?: Item[] }) {
  const t = useT();
  const faces = waitingEmployees(items ?? []).slice(0, 4);
  return (
    <header className="inbox-header">
      <div className="inbox-title">
        <p className="body-medium inbox-greeting">{greeting(t, name)}</p>
        <h1 className="display-small">{t('navInbox')}</h1>
        {/* While the list loads, nobody can say yet that nothing waits. */}
        {items ? (
          <p className="body-medium muted">{t('inboxWaiting', { count: items.length })}</p>
        ) : (
          <div className="inbox-count-loading">
            <Skeleton width={150} height={14} />
          </div>
        )}
      </div>
      {faces.length > 0 && <AvatarStack people={faces} size={34} />}
    </header>
  );
}

function WorkspaceFilter({
  workspaces,
  selected,
  onSelect,
}: {
  workspaces: Workspace[];
  selected?: string;
  onSelect: (id: string | undefined) => void;
}) {
  const t = useT();
  return (
    <div className="inbox-filters">
      <button type="button" className="chip" aria-pressed={selected === undefined} onClick={() => onSelect(undefined)}>
        {t('filterAll')}
      </button>
      {workspaces.map((w) => (
        <button key={w.id} type="button" className="chip" aria-pressed={selected === w.id} onClick={() => onSelect(w.id)}>
          <span className="chip-dot" style={{ background: w.color }} aria-hidden />
          {w.name}
          {w.openCount > 0 && <span className="chip-count">{w.openCount}</span>}
        </button>
      ))}
    </div>
  );
}

/** Loads the next page when the end of the list comes into sight, as the mobile app's list does. */
function LoadMore({ loading, onVisible }: { loading: boolean; onVisible: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useEffectEvent(onVisible);
  useEffect(() => {
    const el = ref.current;
    if (!el || loading || typeof IntersectionObserver === 'undefined') return;
    // A new observer after each page reports at once whether the end is still in sight.
    const io = new IntersectionObserver(
      (seen) => {
        if (seen.some((s) => s.isIntersecting)) visible();
      },
      { rootMargin: '240px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loading]);
  return (
    <div ref={ref} className="inbox-more">
      <LoaderCircle className="spin" size={26} aria-hidden />
    </div>
  );
}

/** Everyone resting: the inbox is clear. */
function RestingTeam() {
  const team = [
    { avatar: 'facebook_manager', color: '#3E63DD', size: 48 },
    { avatar: 'secretary', color: '#8E4EC6', size: 64 },
    { avatar: 'sales', color: '#C08A00', size: 48 },
  ];
  return (
    <div className="resting-team">
      {team.map((m) => (
        <EmployeeAvatar key={m.avatar} avatar={m.avatar} color={m.color} size={m.size} mood="happy" />
      ))}
    </div>
  );
}

/** The right side with nothing open: who is waiting, quietly, and how to move with the keyboard. */
function PanePlaceholder({ items }: { items?: Item[] }) {
  const t = useT();
  const faces = waitingEmployees(items ?? []).slice(0, 5);
  return (
    <div className="pane-placeholder">
      {faces.length > 0 ? (
        <AvatarStack people={faces} size={48} />
      ) : (
        <EmployeeAvatar avatar="secretary" color="#8E4EC6" size={72} mood={items ? 'happy' : 'normal'} />
      )}
      {faces.length > 0 && <p className="body-medium muted pane-hint">{t('inboxPickHint')}</p>}
    </div>
  );
}
